import { useState, useEffect } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Badge } from '@/components/ui/badge'
import { Settings, Download, Plus } from 'lucide-react'

interface CreativeMatrixProps {
  assets: any[]
  brdRules: any[]
  onUpdateAsset?: (assetId: string, updates: any) => void
}

interface MatrixRow {
  id: string
  conceptNumber: number
  creativeStatus: 'Adding' | 'Existing' | 'Remove'
  activationTeam: 'Programmatic' | 'Partner Direct'
  customerType: 'DTC' | 'HCP'
  language: 'ENG' | 'SPA'
  ctaDescription: string
  url: string
  creativeType: 'HTML5 Banner (HTML BAN)' | 'Static Banner (ST BAN)'
  fdaFilingNumber: string
  creativeAgency: string
  campaignType: 'Branded (BR)' | 'Unbranded (UB)'
  creativeCampaignName: string
  creativeContentName: string
  version: string
  indication: string
  reutilization: 'N' | 'R'
  approvedForAdChoices: 'Y' | 'N'
  dofuExpiration: string
  creativeSize: string
  colabCfID: string
  colabCreativeName: string
  asset?: any
}

const creativeSizes = ['300x600', '300x250', '160x600', '728x90', '320x50']
const defaultConcept: Partial<MatrixRow> = {
  creativeStatus: 'Adding',
  activationTeam: 'Programmatic',
  customerType: 'DTC',
  language: 'ENG',
  creativeType: 'HTML5 Banner (HTML BAN)',
  campaignType: 'Branded (BR)',
  version: 'V1',
  reutilization: 'N',
  approvedForAdChoices: 'Y'
}

export default function CreativeMatrix({ assets, brdRules }: CreativeMatrixProps) {
  const [matrixData, setMatrixData] = useState<MatrixRow[]>([])
  const [selectedConcept, setSelectedConcept] = useState<number>(1)
  const [country, setCountry] = useState('USA')
  const [advertiserNameId, setAdvertiserNameId] = useState('Pfizer_Example_12345678_Use_for_OA')

  // Initialize matrix with assets
  useEffect(() => {
    if (assets.length > 0) {
      const initialMatrix: MatrixRow[] = []
      
      assets.forEach((asset, index) => {
        const conceptNumber = Math.floor(index / creativeSizes.length) + 1
        creativeSizes.forEach(size => {
          const row: MatrixRow = {
            id: `${asset.id}-${size}`,
            conceptNumber,
            ...defaultConcept,
            ctaDescription: 'Image',
            url: 'https://www.example.com',
            fdaFilingNumber: asset.analysis?.fdaFilingNumber || 'PP-XXX-USA-0000',
            creativeAgency: 'Agency Name',
            creativeCampaignName: asset.analysis?.campaignName || 'Campaign Name',
            creativeContentName: asset.name?.split('.')[0] || 'Content Name',
            indication: asset.analysis?.customerType === 'HCP' ? 'Medical Indication' : 'General Use',
            creativeSize: size,
            colabCfID: '#VALUE!', // Would be calculated
            colabCreativeName: generateTaxonomyString(asset, size),
            asset
          } as MatrixRow
          
          initialMatrix.push(row)
        })
      })
      
      setMatrixData(initialMatrix)
    }
  }, [assets])

  const generateTaxonomyString = (asset: any, size: string) => {
    // Generate taxonomy string like: ABR_HTML BAN_PP-A1G-USA-0332_WUN_300X600_300x600_BR_ENG_V1_F0002ZAB_EBREATH_TRUMPET_DTC_Trumpet
    const parts = [
      'ABR', // Brand prefix
      'HTML BAN', // Creative type
      'PP-XXX-USA-0000', // FDA Filing
      'AGY', // Agency
      size.toUpperCase().replace('x', 'X'),
      size,
      'BR', // Branded
      'ENG', // Language
      'V1', // Version
      'F0002ZAA', // Generated ID
      (asset.analysis?.campaignName || 'CAMPAIGN').toUpperCase().replace(/\s+/g, ''),
      (asset.name?.split('.')[0] || 'CONTENT').toUpperCase().replace(/\s+/g, ''),
      'DTC',
      asset.name?.split('.')[0] || 'Content'
    ]
    return parts.join('_')
  }

  const getConceptRows = (conceptNumber: number) => {
    return matrixData.filter(row => row.conceptNumber === conceptNumber)
  }

  const updateMatrixRow = (rowId: string, field: string, value: any) => {
    setMatrixData(prev => prev.map(row => 
      row.id === rowId ? { ...row, [field]: value } : row
    ))
  }

  const addNewConcept = () => {
    const maxConcept = Math.max(...matrixData.map(row => row.conceptNumber), 0)
    const newConceptNumber = maxConcept + 1
    
    const newRows: MatrixRow[] = creativeSizes.map(size => ({
      id: `new-${newConceptNumber}-${size}`,
      conceptNumber: newConceptNumber,
      ...defaultConcept,
      ctaDescription: '',
      url: '',
      fdaFilingNumber: '',
      creativeAgency: '',
      creativeCampaignName: '',
      creativeContentName: '',
      indication: '',
      creativeSize: size,
      colabCfID: '#VALUE!',
      colabCreativeName: ''
    } as MatrixRow))
    
    setMatrixData(prev => [...prev, ...newRows])
    setSelectedConcept(newConceptNumber)
  }

  const conceptNumbers = [...new Set(matrixData.map(row => row.conceptNumber))].sort((a, b) => a - b)

  return (
    <div className="space-y-6">
      {/* Header Info */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            Creative Ad Trafficking Matrix
            <div className="flex gap-2">
              <Button variant="outline" size="sm">
                <Settings className="w-4 h-4 mr-2" />
                Configure BRD Rules
              </Button>
              <Button variant="outline" size="sm">
                <Download className="w-4 h-4 mr-2" />
                Export Matrix
              </Button>
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div>
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 block">
                Country
              </label>
              <Input 
                value={country} 
                onChange={(e) => setCountry(e.target.value)}
                className="font-mono"
              />
            </div>
            <div className="md:col-span-2">
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 block">
                Advertiser Name/ID
              </label>
              <Input 
                value={advertiserNameId} 
                onChange={(e) => setAdvertiserNameId(e.target.value)}
                className="font-mono"
              />
            </div>
          </div>

          <div className="flex items-center gap-4 mb-4">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium">Phase 1:</span>
              <Badge variant="outline">Initial Known Campaign Inputs</Badge>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium">Phase 2:</span>
              <Badge variant="outline">Tracking Inputs (post Taxonomy)</Badge>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium">Phase 3:</span>
              <Badge variant="outline">Final Inputs (post AFU)</Badge>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Concept Selector */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <span className="text-sm font-medium">Concept:</span>
          <div className="flex gap-1">
            {conceptNumbers.map(num => (
              <Button
                key={num}
                variant={selectedConcept === num ? "default" : "outline"}
                size="sm"
                onClick={() => setSelectedConcept(num)}
              >
                {num}
              </Button>
            ))}
          </div>
        </div>
        <Button onClick={addNewConcept} size="sm">
          <Plus className="w-4 h-4 mr-2" />
          Add Concept
        </Button>
      </div>

      {/* Creative Matrix Table */}
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-xs border-collapse">
              <thead>
                <tr className="bg-gray-100 dark:bg-gray-800">
                  <th className="border border-gray-300 p-2 text-left font-medium">Concept Number</th>
                  <th className="border border-gray-300 p-2 text-left font-medium">Creative Status</th>
                  <th className="border border-gray-300 p-2 text-left font-medium">Activation Team</th>
                  <th className="border border-gray-300 p-2 text-left font-medium">Customer Type</th>
                  <th className="border border-gray-300 p-2 text-left font-medium">Language</th>
                  <th className="border border-gray-300 p-2 text-left font-medium">CTA/Button Description</th>
                  <th className="border border-gray-300 p-2 text-left font-medium">URL</th>
                  <th className="border border-gray-300 p-2 text-left font-medium">Creative Type</th>
                  <th className="border border-gray-300 p-2 text-left font-medium">FDA Filing Number</th>
                  <th className="border border-gray-300 p-2 text-left font-medium">Creative Agency</th>
                  <th className="border border-gray-300 p-2 text-left font-medium">Campaign Type</th>
                  <th className="border border-gray-300 p-2 text-left font-medium">Creative Campaign Name</th>
                  <th className="border border-gray-300 p-2 text-left font-medium">Creative Content Name</th>
                  <th className="border border-gray-300 p-2 text-left font-medium">Version</th>
                  <th className="border border-gray-300 p-2 text-left font-medium">Indication</th>
                  <th className="border border-gray-300 p-2 text-left font-medium">Reutilization</th>
                  <th className="border border-gray-300 p-2 text-left font-medium">Approved for AdChoices?</th>
                  <th className="border border-gray-300 p-2 text-left font-medium">DOFU Expiration</th>
                  <th className="border border-gray-300 p-2 text-left font-medium">Creative Size</th>
                  <th className="border border-gray-300 p-2 text-left font-medium">CoLab CfID</th>
                  <th className="border border-gray-300 p-2 text-left font-medium">CoLab Creative Name</th>
                </tr>
              </thead>
              <tbody>
                {getConceptRows(selectedConcept).map((row) => (
                  <tr key={row.id} className="hover:bg-gray-50 dark:hover:bg-gray-800">
                    <td className="border border-gray-300 p-2 text-center font-semibold">
                      {row.conceptNumber}
                    </td>
                    <td className="border border-gray-300 p-1">
                      <Select
                        value={row.creativeStatus}
                        onValueChange={(value) => updateMatrixRow(row.id, 'creativeStatus', value)}
                      >
                        <SelectTrigger className="h-8 text-xs">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Adding">Adding</SelectItem>
                          <SelectItem value="Existing">Existing</SelectItem>
                          <SelectItem value="Remove">Remove</SelectItem>
                        </SelectContent>
                      </Select>
                    </td>
                    <td className="border border-gray-300 p-1">
                      <Select
                        value={row.activationTeam}
                        onValueChange={(value) => updateMatrixRow(row.id, 'activationTeam', value)}
                      >
                        <SelectTrigger className="h-8 text-xs">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Programmatic">Programmatic</SelectItem>
                          <SelectItem value="Partner Direct">Partner Direct</SelectItem>
                        </SelectContent>
                      </Select>
                    </td>
                    <td className="border border-gray-300 p-1">
                      <Select
                        value={row.customerType}
                        onValueChange={(value) => updateMatrixRow(row.id, 'customerType', value)}
                      >
                        <SelectTrigger className="h-8 text-xs">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="DTC">DTC</SelectItem>
                          <SelectItem value="HCP">HCP</SelectItem>
                        </SelectContent>
                      </Select>
                    </td>
                    <td className="border border-gray-300 p-1">
                      <Select
                        value={row.language}
                        onValueChange={(value) => updateMatrixRow(row.id, 'language', value)}
                      >
                        <SelectTrigger className="h-8 text-xs">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="ENG">ENG</SelectItem>
                          <SelectItem value="SPA">SPA</SelectItem>
                        </SelectContent>
                      </Select>
                    </td>
                    <td className="border border-gray-300 p-1">
                      <Input
                        value={row.ctaDescription}
                        onChange={(e) => updateMatrixRow(row.id, 'ctaDescription', e.target.value)}
                        className="h-8 text-xs"
                        placeholder="Image"
                      />
                    </td>
                    <td className="border border-gray-300 p-1">
                      <Input
                        value={row.url}
                        onChange={(e) => updateMatrixRow(row.id, 'url', e.target.value)}
                        className="h-8 text-xs"
                        placeholder="https://..."
                      />
                    </td>
                    <td className="border border-gray-300 p-1">
                      <Select
                        value={row.creativeType}
                        onValueChange={(value) => updateMatrixRow(row.id, 'creativeType', value)}
                      >
                        <SelectTrigger className="h-8 text-xs">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="HTML5 Banner (HTML BAN)">HTML5 Banner</SelectItem>
                          <SelectItem value="Static Banner (ST BAN)">Static Banner</SelectItem>
                        </SelectContent>
                      </Select>
                    </td>
                    <td className="border border-gray-300 p-1">
                      <Input
                        value={row.fdaFilingNumber}
                        onChange={(e) => updateMatrixRow(row.id, 'fdaFilingNumber', e.target.value)}
                        className="h-8 text-xs font-mono"
                        placeholder="PP-XXX-USA-0000"
                      />
                    </td>
                    <td className="border border-gray-300 p-1">
                      <Input
                        value={row.creativeAgency}
                        onChange={(e) => updateMatrixRow(row.id, 'creativeAgency', e.target.value)}
                        className="h-8 text-xs"
                        placeholder="Agency Name"
                      />
                    </td>
                    <td className="border border-gray-300 p-1">
                      <Select
                        value={row.campaignType}
                        onValueChange={(value) => updateMatrixRow(row.id, 'campaignType', value)}
                      >
                        <SelectTrigger className="h-8 text-xs">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Branded (BR)">Branded (BR)</SelectItem>
                          <SelectItem value="Unbranded (UB)">Unbranded (UB)</SelectItem>
                        </SelectContent>
                      </Select>
                    </td>
                    <td className="border border-gray-300 p-1">
                      <Input
                        value={row.creativeCampaignName}
                        onChange={(e) => updateMatrixRow(row.id, 'creativeCampaignName', e.target.value)}
                        className="h-8 text-xs"
                        placeholder="Campaign Name"
                      />
                    </td>
                    <td className="border border-gray-300 p-1">
                      <Input
                        value={row.creativeContentName}
                        onChange={(e) => updateMatrixRow(row.id, 'creativeContentName', e.target.value)}
                        className="h-8 text-xs"
                        placeholder="Content Name"
                      />
                    </td>
                    <td className="border border-gray-300 p-1">
                      <Input
                        value={row.version}
                        onChange={(e) => updateMatrixRow(row.id, 'version', e.target.value)}
                        className="h-8 text-xs"
                        placeholder="V1"
                      />
                    </td>
                    <td className="border border-gray-300 p-1">
                      <Input
                        value={row.indication}
                        onChange={(e) => updateMatrixRow(row.id, 'indication', e.target.value)}
                        className="h-8 text-xs"
                        placeholder="Indication"
                      />
                    </td>
                    <td className="border border-gray-300 p-1">
                      <Select
                        value={row.reutilization}
                        onValueChange={(value) => updateMatrixRow(row.id, 'reutilization', value)}
                      >
                        <SelectTrigger className="h-8 text-xs">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="N">N (New)</SelectItem>
                          <SelectItem value="R">R (Reused)</SelectItem>
                        </SelectContent>
                      </Select>
                    </td>
                    <td className="border border-gray-300 p-1">
                      <Select
                        value={row.approvedForAdChoices}
                        onValueChange={(value) => updateMatrixRow(row.id, 'approvedForAdChoices', value)}
                      >
                        <SelectTrigger className="h-8 text-xs">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Y">Y</SelectItem>
                          <SelectItem value="N">N</SelectItem>
                        </SelectContent>
                      </Select>
                    </td>
                    <td className="border border-gray-300 p-1">
                      <Input
                        type="date"
                        value={row.dofuExpiration}
                        onChange={(e) => updateMatrixRow(row.id, 'dofuExpiration', e.target.value)}
                        className="h-8 text-xs"
                      />
                    </td>
                    <td className="border border-gray-300 p-1 text-center font-mono">
                      {row.creativeSize}
                    </td>
                    <td className="border border-gray-300 p-1 text-center text-gray-500">
                      {row.colabCfID}
                    </td>
                    <td className="border border-gray-300 p-1">
                      <div className="text-xs font-mono text-gray-600 truncate max-w-xs">
                        {row.colabCreativeName}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* BRD Rules Applied */}
      {brdRules.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-sm">Applied BRD Rules ({brdRules.length})</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-xs">
              {brdRules.map((rule, index) => (
                <div key={index} className="flex items-center gap-2">
                  <div className={`w-2 h-2 rounded-full ${
                    rule.severity === 'critical' ? 'bg-red-500' :
                    rule.severity === 'warning' ? 'bg-yellow-500' : 'bg-blue-500'
                  }`} />
                  <span className="truncate">{rule.rule}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  )
}
